import { useState, useCallback, useRef, useEffect } from "react";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import { checkPassword } from "api/authAPI";
import { putChangeProfile } from "api/userAPI";
import styled from "styled-components";
import { COLORS } from "styles/colors";
import { calcRem } from "styles/theme";
import { FLEX } from "styles/flex";
import { checkPasswordNumberRegEx, checkPasswordRegEx } from "utils/regEx";
import { useNavigate } from "react-router-dom";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import PasswordBubble from "components/blocks/bubble/PasswordBubble";
import useStatusMutation from "hook/useStatusMutation";
import PasswordInput from "components/atoms/input/PasswordInput";
import { checkEmpty } from "utils/functions";

export type Props = {
  email: string;
  phone: string;
  userId: string;
  modalType: string;
  onModal: (type: string) => void;
};

export default function ChangeModal({
  onModal,
  modalType,
  ...props
}: Props): JSX.Element {
  const navigate = useNavigate();
  const {
    handleErrorSubReason,
    intlString,
    lang,
    notifySuccess,
    displayError,
    createCustomError,
  } = useApiError();

  const [currentPwd, setCurrentPwd] = useState<string>("");
  const [inputPwd, setInputPwd] = useState<string>("");
  const [inputRePwd, setInputRePwd] = useState<string>("");
  const [isValidatedMsg, setIsValidatedMsg] = useState<string>("");
  const [isPwValidated, setIsPwValidated] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (document.activeElement === passwordRef.current) {
      if (
        !(inputPwd.length < 8) &&
        checkPasswordRegEx(inputPwd) &&
        checkPasswordNumberRegEx(inputPwd)
      ) {
        let timer = setTimeout(() => {
          setIsFocus(false);
        }, 800);
        return () => {
          clearTimeout(timer);
        };
      } else {
        setIsFocus(true);
      }
    }
  }, [inputPwd]);

  const mutationCheckPassword = useStatusMutation(checkPassword, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        if (checkEmpty(inputPwd)) {
          setIsPwValidated({
            ...isPwValidated,
            confirm: false,
            current: false,
            new: true,
          });
          setIsValidatedMsg(
            intlString(lang.validateMsg.profile.newPasswordEmpty)
          );
          return;
        }
        if (currentPwd === inputPwd) {
          setIsPwValidated({
            ...isPwValidated,
            current: false,
            new: false,
            confirm: true,
          });
          setIsValidatedMsg(intlString(lang.validateMsg.profile.passwordSame));
          return;
        }

        if (inputPwd.length < 8) {
          setIsPwValidated({
            ...isPwValidated,
            confirm: false,
            current: false,
            new: true,
          });
          setIsValidatedMsg(intlString(lang.validateMsg.signup.password.short));
          return;
        }

        if (!checkPasswordRegEx(inputPwd)) {
          setIsPwValidated({
            ...isPwValidated,
            confirm: false,
            current: false,
            new: true,
          });
          setIsValidatedMsg(
            intlString(lang.validateMsg.signup.password.NotContainUppercase)
          );
          return;
        }

        if (!checkPasswordNumberRegEx(inputPwd)) {
          setIsPwValidated({
            ...isPwValidated,
            confirm: false,
            current: false,
            new: true,
          });
          setIsValidatedMsg(
            intlString(lang.validateMsg.signup.password.NotContainSpecial)
          );
          return;
        }

        if (inputRePwd !== inputPwd) {
          setIsPwValidated({
            ...isPwValidated,
            current: false,
            new: false,
            confirm: true,
          });
          setIsValidatedMsg(
            intlString(lang.validateMsg.signup.passwordMismatch)
          );
          return;
        }
        mutationChangeProfile.mutate({
          userId: props.userId,
          type: modalType,
          payload: {
            currentPassword: currentPwd,
            newPassword: inputPwd,
          },
        });
      } else {
        const error = createCustomError(
          res,
          intlString(lang.validateMsg.login.password.incorrect)
        );

        setIsPwValidated({
          ...isPwValidated,
          current: true,
          new: false,
          confirm: false,
        });
        setIsValidatedMsg(
          displayError(
            error,
            intlString(lang.validateMsg.login.password.incorrect)
          )
        );
        return;
      }
    },
    onError: (err: CustomError) => {
      setIsPwValidated({
        ...isPwValidated,
        current: true,
        new: false,
        confirm: false,
      });
      setIsValidatedMsg(
        displayError(err, intlString(lang.validateMsg.login.password.incorrect))
      );
    },
  });

  const mutationChangeProfile = useStatusMutation(putChangeProfile, {
    onSuccess: (res: any, variables: any) => {
      if (res.error === 0) {
        notifySuccess(lang.label.profile.notify.update.success);
        if (variables?.type === "email") {
          navigate("/login", { replace: true });
        }
        onModal(modalType);
      }
    },
    onError: (err: CustomError) => {
      handleErrorSubReason(
        err,
        intlString(lang.label.profile.notify.update.fail)
      );
    },
  });
  // console.log(isValidatedMsg);
  const onClickBtn = useCallback(async () => {
    if (!mutationChangeProfile.idle) {
      return;
    }
    if (!mutationCheckPassword.idle) {
      return;
    }
    if (checkEmpty(currentPwd)) {
      setIsPwValidated({
        ...isPwValidated,
        current: true,
      });
      setIsValidatedMsg(intlString(lang.validateMsg.profile.passwordEmpty));
      return;
    }
    mutationCheckPassword.mutate({
      payload: {
        email: props.email,
        password: currentPwd,
      },
    });
  }, [
    currentPwd,
    intlString,
    isPwValidated,
    lang.validateMsg.profile.passwordEmpty,
    mutationChangeProfile.idle,
    mutationCheckPassword,
    props.email,
  ]);

  const onChangeUserInfo = useCallback((type: number, value: string) => {
    switch (type) {
      case 2:
        setCurrentPwd(value);
        break;
      case 3:
        setInputPwd(value);
        break;
      case 4:
        setInputRePwd(value);
        break;
      default:
        break;
    }
  }, []);

  return (
    <SmallPopupTemplate
      title="Change Your Password"
      onModal={() => onModal(modalType)}
      onClick={onClickBtn}
      cancel
      data-testid="setting-my-profile-change-small-popup-template-click"
    >
      <PasswordWrapper>
        <ModalInput
          label="Current Password"
          noBold
          essential
          isValidated={isPwValidated.current}
          validatedWord={isValidatedMsg}
        >
          <PasswordInput
            placeholder="Enter your password"
            maxLength={100}
            value={currentPwd}
            onChange={(e) => onChangeUserInfo(2, e.target.value)}
            onEnter={() => {}}
            isValidated={isPwValidated.current}
            data-testid="setting-my-profile-change-password-input-change-user-info"
          />
        </ModalInput>
        <ModalInput
          label="New Password"
          noBold
          essential
          isValidated={isPwValidated.new}
          validatedWord={isValidatedMsg}
        >
          <PasswordInputInner>
            <PasswordInput
              ref={passwordRef}
              className="input-field"
              placeholder="Password"
              maxLength={100}
              value={inputPwd}
              onChange={(e) => onChangeUserInfo(3, e.target.value)}
              onFocus={() => setIsFocus(true)}
              onBlur={() => setIsFocus(false)}
              isValidated={isPwValidated.new}
              onEnter={() => {}}
              data-testid="setting-my-profile-change-password-input-change-user-info-2"
            />

            {isFocus && (
              <Bubble>
                <PasswordBubble password={inputPwd} />
              </Bubble>
            )}
          </PasswordInputInner>
        </ModalInput>
        <ModalInput
          label="Confirm New Password"
          noBold
          essential
          isValidated={isPwValidated.confirm}
          validatedWord={isValidatedMsg}
        >
          <PasswordInput
            placeholder="Re-enter new password"
            maxLength={100}
            onChange={(e) => onChangeUserInfo(4, e.target.value)}
            onEnter={() => {}}
            isValidated={isPwValidated.confirm}
            value={inputRePwd}
            data-testid="setting-my-profile-change-password-input-change-user-info-3"
          />
        </ModalInput>
      </PasswordWrapper>
    </SmallPopupTemplate>
  );
}

export const commonLoginHeight = "40px";

export const PasswordInputInner = styled.div`
  position: relative;
  width: 100%;

  > input {
    color: ${COLORS.BLACK};
    font-size: ${calcRem(14)};
  }
`;

export const Bubble = styled.p`
  position: absolute;
  /* width: 60%; */
  margin-top: 10px;
  z-index: 1;

  ul {
    width: 100%;
    ${FLEX.FlexStartStart};
    flex-direction: column;
    gap: 8px;
    padding: 10px 0;
  }
`;

export const PasswordText = styled.li`
  ${FLEX.FlexCenterCenter};
  gap: 8px;
  font-size: ${calcRem(15)};

  > img {
    width: 15px;
    height: 15px;
  }
`;

export const PasswordTitle = styled.div`
  ${FLEX.FlexCenterCenter};
  gap: 10px;
  padding-bottom: 8px;

  > .title {
    margin-bottom: 0;
  }
`;

export const VerifyWrapper = styled.div`
  padding-bottom: 10px;
  width: 80%;
  margin: 0 auto;
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
`;

export const PasswordWrapper = styled.div`
  width: 450px;
  max-width: 100%;
  position: relative;
  margin 0 auto;
`;
